import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import PageHeader from '../../components/shared/page-header';
import PageBody from '../../components/shared/page-body';
import PageFooter from '../../components/shared/page-footer';
import PageTableContainer from '../../components/shared/page-table-container';
import TiminatorTableV2 from '../../components/shared/table-v2';
import IconTextLarge from '../../components/shared/large-icon-text';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useInfinitePagination } from '../../hooks/useInfinitePagination';
import { deleteProject, getProjectTabs, getProjects, setSelectedProjectType } from '../../features/projects';
import { Project } from '../../features/projects/types';
import { DeleteDialog } from '../../components/shared/delete-dialog';
import { Lx } from '../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import { useProjectsTable } from './hook/useProjectsTable';
import ProjectHeader from './components/project-header';
import { ProjectTypeFilter, MobileView, DEFAULT_PROJECT_FILTER_STATE } from './components/project-type-filter';
import styles from './projects.module.scss';
import { SVGS } from '../../assets/images';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import { canManageProjects } from '../../features/profile/entityPermissions';

export default function ProjectsPage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const { typeId: routeTypeId } = useParams<{ typeId?: string }>();
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const projectsState = useAppSelector(store => store.projects);
    const types = projectsState.types;
    const projects = projectsState.items;
    const selectedTypeId = projectsState.selectedTypeId ?? null;
    const profile = useAppSelector(store => store.profile);

    const [mobileView, setMobileView] = useState<MobileView>('list');
    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);

    useEffect(() => {
        dispatch(getProjectTabs());
    }, [dispatch]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const selectedType = useMemo(
        () => types.find(type => type.id === selectedTypeId) ?? null,
        [types, selectedTypeId],
    );
    const canEditProjects = canManageProjects(profile);

    useEffect(() => {
        if (!types.length) {
            return;
        }

        if (routeTypeId) {
            const numeric = Number(routeTypeId);
            if (!Number.isNaN(numeric) && numeric !== selectedTypeId) {
                dispatch(setSelectedProjectType(numeric));
            }
            return;
        }

        const firstTypeId = types[0]?.id;
        if (firstTypeId == null) {
            return;
        }

        if (firstTypeId !== selectedTypeId) {
            dispatch(setSelectedProjectType(firstTypeId));
        }

        const targetPath = NAVIGATION_ROUTES.PROJECTS_BY_TYPE.replace(':typeId', String(firstTypeId));
        navigate(targetPath, { replace: true });
    }, [dispatch, navigate, routeTypeId, selectedTypeId, types]);

    const fetchProjectsPage = useCallback(
        (page: number) => {
            if (!selectedTypeId) {
                return Promise.resolve();
            }
            return dispatch(getProjects({ projectTypeId: selectedTypeId, page })).unwrap();
        },
        [dispatch, selectedTypeId],
    );

    const hasMore = projects.length < projectsState.total;

    const {
        loadInitial,
        loadNext,
        isLoadingMore,
        isLoadingInitial,
        reset,
    } = useInfinitePagination({
        hasMore,
        fetchPage: fetchProjectsPage,
    });

    useEffect(() => {
        if (!selectedTypeId) {
            return;
        }

        setHasCompletedInitialLoad(false);
        reset();
        loadInitial().catch(() => {
            /* handled elsewhere */
        }).finally(() => {
            setHasCompletedInitialLoad(true);
        });
    }, [selectedTypeId, loadInitial, reset]);

    const handleTypeChange = useCallback((typeId: number) => {
        setMobileView('list');
        dispatch(setSelectedProjectType(typeId));
        const path = NAVIGATION_ROUTES.PROJECTS_BY_TYPE.replace(':typeId', String(typeId));
        navigate(path);
    }, [dispatch, navigate]);

    useEffect(() => {
        if (selectedTypeId == null) {
            return;
        }

        setSearch('');
        setSelectedStatus(-1);
    }, [selectedTypeId]);

    const handleMobileFilterClick = useCallback(() => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    }, []);

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleRowClick = useCallback((project: Project) => {
        if (!canEditProjects) {
            return;
        }

        const typeId = project.projectType.id;
        const editPath = NAVIGATION_ROUTES.EDIT_PROJECT.replace(':typeId', String(typeId));
        navigate(editPath, {
            state: { project },
        });
    }, [canEditProjects, navigate]);

    const handleCreateClick = useCallback(() => {
        if (!canEditProjects || !types.length) {
            return;
        }

        const fallbackType = selectedType ?? types[0];
        const createPath = NAVIGATION_ROUTES.CREATE_PROJECT.replace(':typeId', String(fallbackType.id));
        navigate(createPath);
    }, [canEditProjects, navigate, selectedType, types]);

    const handleDeleteClick = useCallback((project: Project) => {
        if (!canEditProjects) {
            return;
        }

        setProjectToDelete(project);
    }, [canEditProjects]);

    const handleDeleteCancel = useCallback(() => {
        setProjectToDelete(null);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!projectToDelete) {
            return;
        }

        try {
            await dispatch(deleteProject(projectToDelete.id)).unwrap();
            handleDeleteCancel();
            reset();
            await loadInitial();
        } catch {
            handleDeleteCancel();
        }
    }, [dispatch, handleDeleteCancel, loadInitial, projectToDelete, reset]);

    const handleLoadMore = useCallback(() => {
        loadNext().catch(() => {
            /* handled elsewhere */
        });
    }, [loadNext]);

    const tableSettings = useProjectsTable({
        isMobile,
        search,
        selectedStatus,
        canDelete: canEditProjects,
        onDelete: handleDeleteClick,
    });

    const isEmpty = hasCompletedInitialLoad && !isLoadingInitial && tableSettings.isEmpty;
    const showMobileFilterToggle = isMobile;
    const canCreateProjects = canEditProjects && types.length > 0;
    const shouldShowCreateFooterButton = isMobile && mobileView === 'list' && !!selectedType && canCreateProjects;
    const isMobileFilterActive = isMobile && mobileView === 'filter';

    const typeOptions = useMemo(() => types.map(type => ({
        value: String(type.id),
        label: type.name,
        path: NAVIGATION_ROUTES.PROJECTS_BY_TYPE.replace(':typeId', String(type.id)),
    })), [types]);

    const selectedTypeValue = selectedTypeId != null
        ? String(selectedTypeId)
        : (typeOptions[0]?.value ?? '');

    const handleNavigationSelect = useCallback((value: string) => {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
            handleTypeChange(numericValue);
        }
    }, [handleTypeChange]);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const handleMobileHeaderClose = useCallback(() => {
        handleFilterChange(DEFAULT_PROJECT_FILTER_STATE);
    }, [handleFilterChange]);

    const bodyContent = isEmpty ? (
        <div className={styles.emptyState}>
            <IconTextLarge
                text={intl.formatMessage({ id: Lx.Project.NO_PROJECTS })}
                subtext={intl.formatMessage({ id: Lx.Project.CREATE_AND_USE_PROJECT })}
                icon={<SVGS.ProjectsIcon />}
            />
        </div>
    ) : (
        <PageTableContainer>
            <TiminatorTableV2
                columns={tableSettings.columns}
                data={tableSettings.data}
                desktopMinWidth={1055}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                onRowClick={canEditProjects ? handleRowClick : undefined}
                isLoadingMore={isLoadingMore}
            />
        </PageTableContainer>
    );

    return (
        <>
            <PageWrapperV2>
                <PageHeader>
                    <ProjectHeader
                        isMobile={isMobile}
                        showMobileFilterToggle={showMobileFilterToggle}
                        isFilterOpen={isMobileFilterActive}
                        onMobileFilterClick={handleMobileFilterClick}
                        onMobileFilterClose={handleMobileHeaderClose}
                        canCreate={canCreateProjects}
                        onCreateClick={handleCreateClick}
                        typeOptions={typeOptions}
                        selectedTypeValue={selectedTypeValue}
                        onTypeSelect={handleNavigationSelect}
                        loading={isLoadingInitial}
                    />
                </PageHeader>
                <PageBody justifyContent='flex-start' alignItems='stretch'>
                    <ProjectTypeFilter
                        search={search}
                        selectedStatus={selectedStatus}
                        isMobile={isMobile}
                        mobileView={mobileView}
                        onFilterChange={handleFilterChange}
                        onMobileClose={handleMobileFilterClose}
                        className={styles.filterRow}
                    >
                        {bodyContent}
                    </ProjectTypeFilter>
                </PageBody>
                {
                    shouldShowCreateFooterButton && types.length &&
                        <PageFooter>
                            <CreateNewButtonV2 onClick={handleCreateClick} />
                        </PageFooter>
                }
            </PageWrapperV2>
            <DeleteDialog
                open={Boolean(projectToDelete)}
                onCancel={handleDeleteCancel}
                onConfirmDelete={handleDeleteConfirm}
                deleteHeader={intl.formatMessage({ id: Lx.Project.DELETE_PROJECT })}
                deleteMessage={intl.formatMessage({ id: Lx.Project.DELETE_PROJECT_MESSAGE }, { name: projectToDelete?.name })}
                title={projectToDelete?.name ?? intl.formatMessage({ id: Lx.General.PROJECT })}
            />
        </>
    );
}
